import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Button,
    TextField,
    Select,
    MenuItem,
    Typography,
    List,
    ListItem,
    ListItemText,
    Chip,
    CircularProgress,
} from "@material-ui/core";
import { useNotify } from "react-admin";
import { API } from "../../Constant";
import { getConfigGet, getConfigPost } from "../../getConfig";

const SHOP_GRID = API + "/admin/shop-grid";

interface Brand {
    brand: string;
    provider: string;
    status: string;
    category: string | null;
    sprive_category_id: number | null;
}
interface Category {
    id: number;
    display_name: string;
    parent_category_id: number | null;
}

const AssignCategory = () => {
    const notify = useNotify();
    const [brands, setBrands] = useState<Brand[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState<Brand | null>(null);
    const [categoryId, setCategoryId] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [cat, cats]: any[] = await Promise.all([
                    axios.get(SHOP_GRID, getConfigGet({ resource: "catalog" })),
                    axios.get(SHOP_GRID, getConfigGet({ resource: "categories" })),
                ]);
                setBrands(Array.isArray(cat.data) ? cat.data : cat.data?.data || []);
                setCategories(cats.data?.data || cats.data || []);
            } catch (e) {
                notify("Failed to load brands/categories", "warning");
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pick = (b: Brand) => {
        setSelected(b);
        setCategoryId(b.sprive_category_id ? String(b.sprive_category_id) : "");
    };

    const save = async (value: string) => {
        if (!selected) return;
        setSaving(true);
        try {
            await axios.post(
                SHOP_GRID,
                {
                    resource: "assign-category",
                    brand: selected.brand,
                    provider: selected.provider,
                    category_id: value === "" ? null : Number(value),
                },
                { headers: getConfigPost() }
            );
            notify(value === "" ? `Removed sub-category from ${selected.brand}` : `Assigned ${selected.brand}`, "info");
            const newId = value === "" ? null : Number(value);
            const newName = categories.find((c) => String(c.id) === value)?.display_name || null;
            setSelected({ ...selected, sprive_category_id: newId, category: newName });
            setCategoryId(value);
            setBrands((prev) =>
                prev.map((b) =>
                    b.brand === selected.brand && b.provider === selected.provider
                        ? {
                              ...b,
                              sprive_category_id: newId,
                              category: newName,
                          }
                        : b
                )
            );
        } catch (e: any) {
            notify(e?.response?.data?.message || "Failed to assign", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <CircularProgress size={24} />;

    const filtered = brands
        .filter((b) => b.brand.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 100);

    const childCategories = categories.filter((c) => c.parent_category_id != null);
    const parentName = (id: number | null) =>
        categories.find((c) => c.id === id)?.display_name || "";
    const catLabel = (c: Category) =>
        `${c.display_name}  ·  under ${parentName(c.parent_category_id)}`;

    return (
        <Box display="flex" gridGap={24} flexWrap="wrap">
            <Box flex="1 1 320px">
                <Typography variant="subtitle2" gutterBottom>
                    Select a brand
                </Typography>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search brands..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <List dense style={{ maxHeight: 300, overflow: "auto" }}>
                    {filtered.map((b) => (
                        <ListItem
                            button
                            key={b.provider + b.brand}
                            selected={selected?.brand === b.brand && selected?.provider === b.provider}
                            onClick={() => pick(b)}
                        >
                            <ListItemText
                                primary={b.brand}
                                secondary={`${b.provider} · ${b.status}${b.category ? ` · ${b.category}` : ""}`}
                            />
                            {b.category ? (
                                <Chip size="small" label={b.category} />
                            ) : (
                                <Chip size="small" variant="outlined" label="unassigned" />
                            )}
                        </ListItem>
                    ))}
                </List>
            </Box>

            <Box flex="1 1 260px">
                <Typography variant="subtitle2" gutterBottom>
                    Child category
                </Typography>
                {selected ? (
                    (() => {
                        const assigned = childCategories.find(
                            (c) => c.id === selected.sprive_category_id
                        );
                        if (assigned) {
                            return (
                                <>
                                    <Typography variant="body2" gutterBottom>
                                        <b>{selected.brand}</b> is in{" "}
                                        <Chip size="small" label={catLabel(assigned)} />
                                    </Typography>
                                    <Box mt={2}>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            disabled={saving}
                                            onClick={() => save("")}
                                        >
                                            {saving ? "Removing..." : "Remove sub-category"}
                                        </Button>
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                        A brand can only be in one sub-category. Remove to reassign.
                                    </Typography>
                                </>
                            );
                        }
                        return (
                            <>
                                <Typography variant="body2" gutterBottom>
                                    Add <b>{selected.brand}</b> to a sub-category:
                                </Typography>
                                <Select
                                    fullWidth
                                    value={categoryId}
                                    displayEmpty
                                    onChange={(e) => setCategoryId(e.target.value as string)}
                                >
                                    <MenuItem value="">
                                        <em>Select a sub-category…</em>
                                    </MenuItem>
                                    {childCategories.map((c) => (
                                        <MenuItem key={c.id} value={String(c.id)}>
                                            {catLabel(c)}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Box mt={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={saving || categoryId === ""}
                                        onClick={() => save(categoryId)}
                                    >
                                        {saving ? "Adding..." : "Add to sub-category"}
                                    </Button>
                                </Box>
                            </>
                        );
                    })()
                ) : (
                    <Typography variant="body2" color="textSecondary">
                        Pick a brand on the left.
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default AssignCategory;
